// Utility functions for creating and parsing SEO-friendly URLs with author and date info

// Create a URL-friendly slug from a string
const createSlug = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single
    .substring(0, 40); // Limit length to prevent too long URLs
};

// Format date to YYYY&MM-DD format
const formatDateForUrl = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}&${month}-${day}`;
};

// Format author name for URL (firstnameYYYY&MM-middle-lastname)
const formatAuthorForUrl = (authorName, dateString) => {
  if (!authorName) return '';
  
  const datePart = dateString ? formatDateForUrl(dateString) : '';
  const names = authorName.trim().split(/\s+/);
  
  if (names.length === 0) return '';
  
  const firstName = createSlug(names[0]);
  const lastName = names.length > 1 ? createSlug(names[names.length - 1]) : '';
  const middleName = names.length > 2 
    ? names.slice(1, -1).map(n => createSlug(n)).join('-')
    : '';
  
  let result = firstName;
  if (datePart) result += datePart;
  if (middleName) result += `-${middleName}`;
  if (lastName) result += `-${lastName}`;
  
  return result;
};

// Encode ID and create a URL-friendly string
export const encodeId = (id, title = '', author = '', date = '') => {
  if (!id) return '';
  
  // Convert the ID to base64 and make it URL-safe
  const encodedId = btoa(id)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  
  // Create URL parts
  const titleSlug = title ? createSlug(title) : '';
  const authorPart = formatAuthorForUrl(author, date);
  
  // Combine all parts
  const parts = [];
  if (titleSlug) parts.push(titleSlug);
  if (authorPart) parts.push(authorPart);
  parts.push(encodedId);
  
  return parts.join('-');
};

// Decode the ID from a URL string
export const decodeId = (urlString) => {
  if (!urlString) return '';
  
  // The ID is always the last part after the last hyphen
  const lastHyphenIndex = urlString.lastIndexOf('-');
  const encoded = lastHyphenIndex > 0 
    ? urlString.substring(lastHyphenIndex + 1) 
    : urlString;
  
  // Add padding back if needed and convert from URL-safe base64 to standard base64
  let str = encoded.replace(/-/g, '+').replace(/_/g, '/');
  while (str.length % 4) {
    str += '=';
  }
  
  try {
    return atob(str);
  } catch (e) {
    console.error('Error decoding ID:', e);
    return '';
  }
};

// Generate a complete blog post URL
export const generateBlogPostUrl = (id, title, author = '', date = '') => {
  const path = encodeId(id, title, author, date);
  return `/blog-view/${path}`;
};

// Extract metadata from URL (title, author, date)
export const extractMetadataFromUrl = (urlString) => {
  if (!urlString) return {};
  
  const parts = urlString.split('-');
  if (parts.length < 2) return {};
  
  // The last part is always the encoded ID
  const encodedId = parts[parts.length - 1];
  
  // Everything before the ID is the title/author part
  const metaPart = urlString.substring(0, urlString.lastIndexOf('-' + encodedId));
  
  return {
    title: metaPart, // This is a simplified version, you might want to parse it further
    id: decodeId(encodedId)
  };
};
